(() => {
  const initializationKey = 'manatorskProductAjaxCartInitialized';

  if (window[initializationKey]) return;
  window[initializationKey] = true;

  const productFormSelector =
    'block-buy-buttons form[data-type="add-to-cart-form"]';

  document.addEventListener('submit', handleProductSubmit, true);

  async function handleProductSubmit(event) {
    const form = event.target;

    if (!(form instanceof HTMLFormElement) || !form.matches(productFormSelector)) {
      return;
    }

    const submitButton =
      event.submitter || form.querySelector('button[name="add"], .add-to-cart');

    if (
      !(submitButton instanceof HTMLButtonElement) ||
      submitButton.disabled ||
      (event.submitter && !event.submitter.matches('[name="add"], .add-to-cart'))
    ) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    const variantInput = form.querySelector('input[name="id"]');

    if (!(variantInput instanceof HTMLInputElement) || !variantInput.value || variantInput.disabled) {
      return;
    }

    const originalContent = submitButton.innerHTML;
    const originalDisabled = submitButton.disabled;
    const originalMinHeight = submitButton.style.minHeight;
    const renderedButtonHeight = submitButton.getBoundingClientRect().height;

    clearCartMessage(form);
    submitButton.style.minHeight = `${renderedButtonHeight}px`;
    submitButton.disabled = true;
    submitButton.setAttribute('aria-busy', 'true');
    submitButton.innerHTML = spinnerIcon();

    try {
      const formData = new FormData(form);

      if (!formData.has('id')) {
        formData.set('id', variantInput.value);
      }

      if (!formData.has('quantity')) {
        formData.set('quantity', getProductQuantity(form));
      }

      const requestedQuantity = Math.max(1, Math.trunc(Number(formData.get('quantity')) || 1));
      formData.set('quantity', String(requestedQuantity));

      await validateCartLimit(form, variantInput.value, requestedQuantity);

      const response = await fetch(getCartRoute('cart/add.js'), {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData,
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || 'Kunde inte lägga produkten i varukorgen.');
      }

      const addedItem = await response.json();
      let cart = null;

      try {
        cart = await refreshCartCount();
      } catch (cartError) {
        console.warn('Manatorsk product cart: cart count could not be refreshed.', cartError);
      }

      submitButton.innerHTML = checkIcon();

      document.dispatchEvent(
        new CustomEvent('manatorsk:product-added', {
          bubbles: true,
          detail: { product: addedItem, cart },
        }),
      );

      await wait(1100);
    } catch (error) {
      console.error('Manatorsk product cart: add to cart failed.', error);
      showCartMessage(
        form,
        submitButton,
        error.message || 'Kunde inte lägga till produkten.',
      );
    } finally {
      submitButton.innerHTML = originalContent;
      submitButton.disabled = originalDisabled;
      submitButton.removeAttribute('aria-busy');
      submitButton.style.minHeight = originalMinHeight;
    }
  }

  function getProductQuantity(form) {
    const associatedQuantity = form.elements.namedItem('quantity');

    if (associatedQuantity instanceof HTMLInputElement && associatedQuantity.value) {
      return associatedQuantity.value;
    }

    const productSection =
      form.closest('.page-content--product') || form.closest('[data-product-id]');
    const sectionQuantity = productSection?.querySelector('input[name="quantity"]');

    if (sectionQuantity instanceof HTMLInputElement && sectionQuantity.value) {
      return sectionQuantity.value;
    }

    return '1';
  }

  async function validateCartLimit(form, variantId, requestedQuantity) {
    const config = getProductCartConfig(form);
    const variant = config.variants.find(
      (candidate) => String(candidate.id) === String(variantId),
    );

    if (!variant) return;

    const limits = [];
    const quantityRuleMax = toLimit(variant.quantityRuleMax);

    if (quantityRuleMax !== null) {
      limits.push(quantityRuleMax);
    }

    if (
      variant.inventoryManagement === 'shopify' &&
      variant.inventoryPolicy !== 'continue'
    ) {
      const inventoryQuantity = toLimit(variant.inventoryQuantity);
      if (inventoryQuantity !== null) limits.push(inventoryQuantity);
    }

    if (!limits.length) return;

    const maximumQuantity = Math.min(...limits);
    const cart = await fetchCart();
    const quantityInCart = cart.items
      .filter((item) => String(item.variant_id) === String(variantId))
      .reduce((total, item) => total + Number(item.quantity || 0), 0);

    if (quantityInCart + requestedQuantity > maximumQuantity) {
      throw new Error(
        config.maxMessage || 'Maxantalet av den här artikeln finns redan i varukorgen.',
      );
    }
  }

  function getProductCartConfig(form) {
    const configElement = form.querySelector('[data-manatorsk-product-cart-config]');

    if (!configElement) return { variants: [] };

    try {
      const config = JSON.parse(configElement.textContent || '{}');
      return {
        ...config,
        variants: Array.isArray(config.variants) ? config.variants : [],
      };
    } catch (error) {
      console.error('Manatorsk product cart: invalid cart configuration.', error);
      return { variants: [] };
    }
  }

  function toLimit(value) {
    if (value === null || value === undefined || value === '') return null;

    const number = Number(value);
    return Number.isFinite(number) && number >= 0 ? Math.trunc(number) : null;
  }

  async function fetchCart() {
    const response = await fetch(getCartRoute('cart.js'), {
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) {
      throw new Error('Kunde inte kontrollera varukorgen. Försök igen.');
    }

    return response.json();
  }

  async function refreshCartCount() {
    const cart = await fetchCart();
    const itemCount = Number(cart.item_count || 0);

    document
      .querySelectorAll('.cart-count, .site-header__cart-count')
      .forEach((element) => updateCountElement(element, itemCount));

    document
      .querySelectorAll('.cart-link__bubble-num, [data-cart-count], [data-cart-item-count]')
      .forEach((element) => updateCountElement(element, itemCount));

    document.querySelectorAll('.cart-link__bubble').forEach((bubble) => {
      bubble.classList.toggle('cart-link__bubble--visible', itemCount > 0);
      bubble.hidden = itemCount === 0;
    });

    return cart;
  }

  function updateCountElement(element, itemCount) {
    const nestedCount = element.querySelector?.(
      '.cart-link__bubble-num, [data-cart-count], [data-cart-item-count], span',
    );

    if (nestedCount && nestedCount !== element) {
      nestedCount.textContent = String(itemCount);
      nestedCount.hidden = itemCount === 0;
    } else {
      element.textContent = String(itemCount);
    }

    element.hidden = itemCount === 0;
  }

  function showCartMessage(form, submitButton, message) {
    let messageElement = form.querySelector('[data-manatorsk-product-cart-message]');

    if (!messageElement) {
      messageElement = document.createElement('div');
      messageElement.className = 'manatorsk-product-cart-message';
      messageElement.dataset.manatorskProductCartMessage = '';
      messageElement.setAttribute('role', 'alert');

      const buttonWrapper = submitButton.closest('element-button') || submitButton;
      buttonWrapper.insertAdjacentElement('afterend', messageElement);
    }

    messageElement.textContent = message;
    messageElement.hidden = false;
  }

  function clearCartMessage(form) {
    const messageElement = form.querySelector('[data-manatorsk-product-cart-message]');
    if (messageElement) messageElement.hidden = true;
  }

  function getCartRoute(path) {
    const root = String(window.Shopify?.routes?.root || '/');
    return `${root.endsWith('/') ? root : `${root}/`}${path}`;
  }

  function checkIcon() {
    return `
      <svg
        aria-hidden="true"
        viewBox="0 0 24 24"
        width="20"
        height="20"
        fill="none"
      >
        <path
          d="m5 12.5 4.2 4.2L19 7"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
        ></path>
      </svg>
      <span class="visually-hidden">Tillagd i varukorgen</span>
    `;
  }

  function spinnerIcon() {
    return `
      <span class="manatorsk-product-cart-spinner" aria-hidden="true"></span>
      <span class="visually-hidden">Lägger till i varukorgen</span>
    `;
  }

  function wait(milliseconds) {
    return new Promise((resolve) => window.setTimeout(resolve, milliseconds));
  }
})();
